import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTier } from '../context/TierContext';
import { portfolioChat, screenerChat } from '../services';
import { isTouchPrimaryDevice } from '../utils/device';
import './PortfolioChat.css';

// sessionStorage key used to hand a question off to /alpha (Workspace) — the
// workspace reads this on mount and auto-creates a conversation seeded with it.
export const ALPHA_WORKSPACE_SEED_KEY = 'aw-seed-question';

/**
 * CopilotChat – Unified floating AI chat bubble.
 *
 * Props:
 *   mode           – 'portfolio' | 'screener'
 *   analysisResult – full API response (portfolio or screener results)
 */

const MODES = {
  portfolio: {
    endpoint: portfolioChat,
    title: 'Alpha · Portfolio',
    subtitle: 'AlphaGrade’s in-app intelligence',
    welcomeIcon: '\u{1F4CA}',
    welcomeTitle: 'Portfolio Analysis Ready',
    welcomeText: 'Your portfolio has been analyzed. Ask Alpha anything about your scores, conviction levels, individual positions, or what moves might make sense.',
    placeholder: 'Ask Alpha about your portfolio...',
    notification: 'Ask Alpha about your portfolio analysis',
    bubbleTitle: 'Ask Alpha about your portfolio',
    headerClass: '',
    notificationClass: '',
    sendBtnClass: '',
    bubbleBtnClass: '',
    wrapperClass: '',
    typingStyle: { background: 'var(--gradient-primary)', color: 'var(--text-inverse)' },
    getSuggestions: (result) => {
      const suggestions = [];
      if (result) {
        suggestions.push('Which holdings have the strongest fundamentals AND most reasonable valuations?');
        suggestions.push('Which positions look stretched on valuation right now?');
        suggestions.push('Which holdings could be value traps — cheap but with weak quality?');
        suggestions.push("What's my biggest concentration or sector risk?");
        suggestions.push('Which positions are best-positioned given current market conditions?');
        const items = result.items || [];
        if (items.length > 0 && items[0]?.symbol) {
          suggestions.push(`Walk me through ${items[0].symbol}'s scores in detail`);
        }
      }
      return suggestions;
    },
  },
  screener: {
    endpoint: screenerChat,
    title: 'Alpha · Screener',
    subtitle: 'AlphaGrade’s in-app intelligence',
    welcomeIcon: '\u{1F50D}',
    welcomeTitle: 'Screening Results Ready',
    welcomeText: 'Your stocks have been screened. Ask Alpha to compare them, explain scores, or identify the best opportunities.',
    placeholder: 'Ask Alpha about screened stocks...',
    notification: 'Ask Alpha about your screened stocks',
    bubbleTitle: 'Ask Alpha about screened stocks',
    headerClass: 'screener-chat-header',
    notificationClass: 'screener-notification',
    sendBtnClass: 'screener-send-btn',
    bubbleBtnClass: 'screener-bubble-btn',
    wrapperClass: 'screener-chat',
    typingStyle: { background: 'linear-gradient(135deg, #FF5F1F, #F59E0B)', color: '#0B0F19' },
    getSuggestions: (result) => {
      const suggestions = [];
      if (result) {
        const items = result.items || [];
        suggestions.push('Which of these has the strongest fundamentals AND most reasonable valuation?');
        suggestions.push('Show me the best peer-relative valuations in this set');
        suggestions.push('Which could be a value trap — cheap but with weak quality?');
        suggestions.push('Which has the strongest sector tailwind right now?');
        suggestions.push('Rank these by conviction and explain the reasoning');
        if (items.length > 0 && items[0]?.symbol) {
          suggestions.push(`Tell me what makes ${items[0].symbol} stand out`);
        }
      }
      return suggestions;
    },
  },
};

export default function CopilotChat({ mode = 'portfolio', analysisResult }) {
  const cfg = MODES[mode] || MODES.portfolio;
  const navigate = useNavigate();
  const { hasFeature } = useTier();
  const canUseWorkspace = hasFeature('alpha_memory');

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [showNotification, setShowNotification] = useState(true);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Handoff: take the current draft (or the last user question if no draft) and
  // hand it to the Workspace, which will spin up a fresh persistent conversation.
  const handleOpenInWorkspace = useCallback(() => {
    const draft = input.trim();
    const lastUser = [...messages].reverse().find((m) => m.role === 'user');
    const seed = draft || lastUser?.content || '';
    if (seed) {
      try {
        sessionStorage.setItem(ALPHA_WORKSPACE_SEED_KEY, seed);
      } catch {}
    }
    setIsOpen(false);
    navigate('/alpha');
  }, [input, messages, navigate]);

  // Auto-hide notification after 5 seconds
  useEffect(() => {
    if (showNotification) {
      const timer = setTimeout(() => setShowNotification(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [showNotification]);

  // Auto-scroll to newest message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Focus input when chat opens
  useEffect(() => {
    if (!isOpen) return undefined;
    const t = setTimeout(() => inputRef.current?.focus(), 200);
    return () => clearTimeout(t);
  }, [isOpen]);

  // Auto-grow textarea to fit content, capped at CSS max-height (100px ≈ 4
  // rows). Keeps long drafts visible instead of clipping to a single row.
  useEffect(() => {
    const ta = inputRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 100)}px`;
  }, [input]);

  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || loading) return;

    const userMsg = { role: 'user', content: text.trim() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setError(null);
    setLoading(true);

    try {
      const data = await cfg.endpoint(userMsg.content, messages, analysisResult);
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: data.reply },
      ]);
    } catch (err) {
      console.error(`${mode} chat error:`, err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [loading, messages, analysisResult, cfg, mode]);

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  // Desktop: Enter sends, Shift+Enter newlines. Touch-first devices
  // (phones/tablets): Enter inserts a newline — too easy to hit by accident —
  // so sending is via the button only. Never send mid-IME-composition.
  const handleKeyDown = (e) => {
    if (
      e.key === 'Enter' &&
      !e.shiftKey &&
      !isTouchPrimaryDevice() &&
      !e.nativeEvent?.isComposing
    ) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const handleSuggestion = (text) => {
    sendMessage(text);
  };

  // Simple markdown-to-JSX renderer
  const renderContent = (text) => {
    if (!text) return null;
    const paragraphs = text.split('\n\n');
    return paragraphs.map((para, pIdx) => {
      const lines = para.split('\n');
      const isList = lines.every(l => l.trim().startsWith('- ') || l.trim().startsWith('* ') || l.trim() === '');
      if (isList && lines.some(l => l.trim().startsWith('- ') || l.trim().startsWith('* '))) {
        return (
          <ul key={pIdx}>
            {lines
              .filter(l => l.trim().startsWith('- ') || l.trim().startsWith('* '))
              .map((l, lIdx) => (
                <li key={lIdx}>{renderInline(l.replace(/^[\s]*[-*]\s/, ''))}</li>
              ))}
          </ul>
        );
      }
      const isNumbered = lines.every(l => /^\s*\d+[.)]\s/.test(l) || l.trim() === '');
      if (isNumbered && lines.some(l => /^\s*\d+[.)]\s/.test(l))) {
        return (
          <ol key={pIdx}>
            {lines
              .filter(l => /^\s*\d+[.)]\s/.test(l))
              .map((l, lIdx) => (
                <li key={lIdx}>{renderInline(l.replace(/^\s*\d+[.)]\s/, ''))}</li>
              ))}
          </ol>
        );
      }
      return (
        <p key={pIdx}>
          {lines.map((line, lIdx) => (
            <React.Fragment key={lIdx}>
              {lIdx > 0 && <br />}
              {renderInline(line)}
            </React.Fragment>
          ))}
        </p>
      );
    });
  };

  const renderInline = (text) => {
    if (!text) return null;
    const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i}>{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith('`') && part.endsWith('`')) {
        return <code key={i}>{part.slice(1, -1)}</code>;
      }
      return part;
    });
  };

  const suggestions = cfg.getSuggestions(analysisResult);

  return (
    <div className={`chat-bubble-wrapper ${cfg.wrapperClass}`}>
      {/* Notification toast */}
      {!isOpen && showNotification && (
        <div className={`chat-notification ${cfg.notificationClass}`}>
          {cfg.notification}
        </div>
      )}

      {/* Floating chat window */}
      {isOpen && (
        <div className="chat-floating-window">
          {/* Header */}
          <div className={`chat-header ${cfg.headerClass}`} onClick={() => setIsOpen(false)}>
            <div className="chat-header-left">
              <div className={`chat-header-icon ${mode === 'screener' ? 'screener-header-icon' : ''}`}>α</div>
              <div>
                <div className="chat-header-title">{cfg.title}</div>
                <div className="chat-header-subtitle">{cfg.subtitle}</div>
              </div>
            </div>
            <div className="chat-header-actions">
              <button
                type="button"
                className="chat-workspace-btn"
                onClick={(e) => { e.stopPropagation(); handleOpenInWorkspace(); }}
                title={canUseWorkspace
                  ? 'Continue this question in Alpha Workspace'
                  : 'Open Alpha Workspace (Premium)'}
                aria-label="Open in Alpha Workspace"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                  <polyline points="15 3 21 3 21 9"/>
                  <line x1="10" y1="14" x2="21" y2="3"/>
                </svg>
                <span>Workspace</span>
              </button>
              <button className="chat-minimize-btn" onClick={(e) => { e.stopPropagation(); setIsOpen(false); }}>&minus;</button>
            </div>
          </div>

          {/* Messages */}
          <div className="chat-messages">
            {messages.length === 0 && !loading && (
              <div className="chat-welcome">
                <span className="chat-welcome-icon">{cfg.welcomeIcon}</span>
                <h4>{cfg.welcomeTitle}</h4>
                <p>{cfg.welcomeText}</p>
                {suggestions.length > 0 && (
                  <div className="chat-suggestions">
                    {suggestions.map((s, i) => (
                      <button
                        key={i}
                        className="chat-suggestion-btn"
                        onClick={() => handleSuggestion(s)}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {messages.map((msg, idx) => (
              <div key={idx} className={`chat-message ${msg.role}`}>
                <div className="chat-avatar">
                  {msg.role === 'user' ? 'U' : 'α'}
                </div>
                <div className="chat-bubble">
                  {msg.role === 'assistant'
                    ? renderContent(msg.content)
                    : msg.content}
                </div>
              </div>
            ))}

            {loading && (
              <div className="chat-typing">
                <div className="chat-avatar" style={cfg.typingStyle}>α</div>
                <div className="chat-bubble">
                  <span className="typing-dot"></span>
                  <span className="typing-dot"></span>
                  <span className="typing-dot"></span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Error display */}
          {error && (
            <div className="chat-error">{error}</div>
          )}

          {/* Input */}
          <form className="chat-input-area" onSubmit={handleSubmit}>
            <textarea
              ref={inputRef}
              className="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={cfg.placeholder}
              rows={1}
              disabled={loading}
            />
            <button
              type="submit"
              className={`chat-send-btn ${cfg.sendBtnClass}`}
              disabled={!input.trim() || loading}
              title="Send message"
            >
              &#9654;
            </button>
          </form>
        </div>
      )}

      {/* Bubble button (visible when chat is closed) */}
      {!isOpen && (
        <button
          className={`chat-bubble-btn ${cfg.bubbleBtnClass}`}
          onClick={() => { setIsOpen(true); setShowNotification(false); }}
          title={cfg.bubbleTitle}
        >
          α
          {messages.length > 0 && (
            <span className="chat-badge">{messages.length}</span>
          )}
        </button>
      )}
    </div>
  );
}
